import { Component, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { NgModel } from "@angular/forms";
import { IPlannedGivingContactModel } from "../domain/types";

@Component({
  selector: "app-planned-giving",
  templateUrl: "./planned-giving.component.html",
  styleUrls: ["./planned-giving.component.scss"],
  standalone: false,
})
export class PlannedGivingComponent implements OnChanges {
  @Input() plannedGiving: IPlannedGivingContactModel;
  @Input() form: any;
  @Input() donorPhone = "";
  @Input() donorEmail = "";

  @ViewChild("plannedGivingPhone") plannedGivingPhoneControl: NgModel;
  @ViewChild("plannedGivingEmail") plannedGivingEmailControl: NgModel;

  readonly fallbackLabelText = "Yes! Please contact me with more information about ways I can give through a will, trust, gift annuity, stocks or other special opportunities.";
  readonly fallbackUseDonorContactText = "Use the same telephone and email address from the personal information section.";

  get hasPlannedGivingPhone(): boolean {
    return !!this.plannedGiving?.plannedGivingPhone?.trim();
  }

  get hasPlannedGivingEmail(): boolean {
    return !!this.plannedGiving?.plannedGivingEmail?.trim();
  }

  get hasErrors(): boolean {
    if (!this.plannedGiving?.plannedGivingInterest) {
      return false;
    }

    const missingContact = !this.hasPlannedGivingPhone && !this.hasPlannedGivingEmail;

    return (
      missingContact ||
      !!this.plannedGivingPhoneControl?.invalid ||
      !!this.plannedGivingEmailControl?.invalid
    );
  }

  togglePlannedGivingInterest(): void {
    this.plannedGiving.plannedGivingInterest =
      !this.plannedGiving.plannedGivingInterest;

    if (!this.plannedGiving.plannedGivingInterest) {
      this.clearContactFields();
    }
  }

  toggleUseDonorContact(): void {
    this.plannedGiving.useDonorContact = !this.plannedGiving.useDonorContact;

    if (this.plannedGiving.useDonorContact) {
      this.copyDonorContactValues();
    }
  }

  onContactEdited(): void {
    // Keep manual edits independent of later donor contact changes.
    this.plannedGiving.useDonorContact = false;
  }

  touchInvalidControls(): void {
    if (!this.plannedGiving?.plannedGivingInterest) {
      return;
    }

    const missingContact = !this.hasPlannedGivingPhone && !this.hasPlannedGivingEmail;

    if (missingContact || this.plannedGivingPhoneControl?.invalid) {
      this.plannedGivingPhoneControl?.control.markAsTouched();
    }

    if (missingContact || this.plannedGivingEmailControl?.invalid) {
      this.plannedGivingEmailControl?.control.markAsTouched();
    }
  }

  showError(ctl: NgModel): boolean {
    return this.form && ctl && (this.form.submitted || ctl.touched || ctl.dirty);
  }

  private clearContactFields(): void {
    this.plannedGiving.plannedGivingPhone = "";
    this.plannedGiving.plannedGivingEmail = "";
    this.plannedGiving.useDonorContact = false;

    if (this.plannedGivingPhoneControl) {
      this.plannedGivingPhoneControl.control.markAsPristine();
      this.plannedGivingPhoneControl.control.markAsUntouched();
    }

    if (this.plannedGivingEmailControl) {
      this.plannedGivingEmailControl.control.markAsPristine();
      this.plannedGivingEmailControl.control.markAsUntouched();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.plannedGiving?.useDonorContact &&
      (changes["donorPhone"] || changes["donorEmail"])
    ) {
      this.copyDonorContactValues();
    }
  }

  private copyDonorContactValues(): void {
    this.plannedGiving.plannedGivingPhone = this.donorPhone || "";
    this.plannedGiving.plannedGivingEmail = this.donorEmail || "";
  }
}
